import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type { OrganizationRoleDeletedDto, ResponseDto } from "@mimber/schema"
import { rolesKeys } from "../roles.utils"
import type { DeleteRoleParams } from "./useDeleteRole.types"

/**
 * Organization-specific hook for deleting an organization role
 * Can only be used in mimber-organization app
 */
export const useDeleteRole = (
  options?: UseMutationOptions<
    ResponseDto<OrganizationRoleDeletedDto>,
    Error,
    DeleteRoleParams
  >
): UseMutationResult<
  ResponseDto<OrganizationRoleDeletedDto>,
  Error,
  DeleteRoleParams
> => {
  guardAppUsage("mimber-organization", "useDeleteRole")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async ({ uuid }: DeleteRoleParams) => {
      const response = await server.delete<
        ResponseDto<OrganizationRoleDeletedDto>
      >(`roles/${uuid}`)
      return response.data
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: rolesKeys.findAll() })
      queryClient.removeQueries({
        queryKey: rolesKeys.findOne(variables.uuid),
      })
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
  })
}
